"use client";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PatientScriptInfo from "../../../global/view/patient-script-infoForm"; 
import { initialScriptData } from "../../../global/view/filter-patient-scriptForm";
import { fetchScriptDetails, fetchProfileFamily, fetchProfileInfo, createProcessPrescription } from "../../../../api/dispensary/script/processScriptApi.js";
import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import { CompletedPopup } from "../../ToFollows/views/FollowUpSignatureFlow.jsx";

export default function FilterAmendScriptInfo() {
  const { scriptNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { profileNo, patientId } = location.state || {};
  const scriptData = initialScriptData.find((s) => s.scriptNo === scriptNo);

  const initialSelectedRows = scriptData?.drugs
    ? Object.fromEntries(scriptData.drugs.map((_, index) => [index, false]))
    : {};
  const [showSelectColumn, setShowSelectColumn] = useState(false);
  const [selectedRows, setSelectedRows] = useState(initialSelectedRows);
  const [rewriteDisabled, setRewriteDisabled] = useState(false);
  const [showNoSelectionModal, setShowNoSelectionModal] = useState(false);
  const [isAmendingNoDrugs, setIsAmendingNoDrugs] = useState(false);
  const [amendError, setAmendError] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [completedMeta, setCompletedMeta] = useState({ prescriptionId: null, receiptNumber: null });

  // Determine if Rewrite Script should be disabled based on beneficiary vs main member logic
  useEffect(() => {
    const checkBeneficiaryEnabled = async () => {
      if (!profileNo || !patientId) return;
      try {
        const [info, family] = await Promise.all([
          fetchProfileInfo(profileNo),
          fetchProfileFamily(profileNo),
        ]);

        const mainIdStr = info?.mainPatient?.id != null
          ? info.mainPatient.id?.toString?.() ?? String(info.mainPatient.id)
          : null;

        const isBeneficiary = mainIdStr ? String(patientId) !== mainIdStr : false;
        const profileEnabled = info?.enabled ?? false;

        if (!profileEnabled) {
          // Disabled profile blocks both main member and beneficiaries
          setRewriteDisabled(true);
          return;
        }

        if (isBeneficiary) {
          const target = (family || []).find((m) => String(m.id) === String(patientId));
          const enabledBen = target?.enabledbeneficiary ?? false;
          setRewriteDisabled(!enabledBen);
        } else {
          // Main member is allowed when profile is enabled
          setRewriteDisabled(false);
        }
      } catch (e) {
        console.error("Failed to load family for amend-script beneficiary check", e);
      }
    };
    checkBeneficiaryEnabled();
  }, [profileNo, patientId]);

  // Compute Script Number display consistently with the list, with safe fallbacks when refreshed
  const t = String(scriptData?.type || '').toLowerCase();
  const displayScriptNo = scriptData
    ? (t === 'process'
        ? (scriptData.scriptNumber || scriptData.processscriptnumber || scriptData.processScriptNumber || scriptData.scriptNo)
        : t === 'clinic'
        ? (scriptData.clinicReference || scriptData.clinicreference || scriptData.scriptNo)
        : scriptData.scriptNo)
    : scriptNo; // fallback to route param so the field is still shown on hard refresh

  const extraFields = [
    { label: "Script Number", value: displayScriptNo || "N/A", readOnly: true },
  ];

  const proceedToNewScriptWithSelection = async () => {
    // Load complete script details to get full drug data, including IDs needed for stockreturn
    let medsForAmend = [];
    let originalDrugsMeta = [];
    let removedDrugsMeta = [];
    let existingDocUrl = null;
    try {
      const details = await fetchScriptDetails(scriptNo);
      const fullDrugs = Array.isArray(details?.drugs) ? details.drugs : [];
      originalDrugsMeta = fullDrugs;

      // Capture existing script document URL (if any) from the DB so amend
      // flows can display it when no new file is uploaded on the form.
      if (details && details.script) {
        existingDocUrl = details.script.docurl || details.script.docUrl || null;
      }

      const selected = fullDrugs.filter((_, idx) => !!selectedRows[idx]);
      const removed = fullDrugs.filter((_, idx) => !selectedRows[idx]);

      medsForAmend = selected.map((d, i) => {
        const codeStr = (d.icd10 || "").trim();
        const codes = codeStr ? codeStr.split(/\s*,\s*/) : [];
        return {
          id: i + 1,
          drugName: d.drugName || "",
          strength: d.strength || "",
          quantity: d.quantity || "1",
          packSize: d.packSize || "",
          icd10Options: codes,
          icd10: codes[0] || "",
          nappiCode: d.nappiCode || "",
          instructions: d.instructions || "",
          costPrice: d.costPrice || "",
          deliveryOption: "Collect",
          // Preserve identifiers so amend logic can distinguish kept vs new rows
          prescriptionDrugId: d.prescriptionDrugId || null,
          sku: d.sku || null,
          fromOriginal: true,
        };
      });

      removedDrugsMeta = removed.map((d) => ({
        prescriptionDrugId: d.prescriptionDrugId || null,
        sku: d.sku || null,
        quantity: d.quantity || 0,
      }));

      if (medsForAmend.length === 0 && (scriptData?.drugs || []).length) {
        // Fallback to basic data if selection mismatch
        const fallbackSel = (scriptData.drugs || []).filter((_, idx) => !!selectedRows[idx]);
        medsForAmend = fallbackSel.map((d, i) => {
          const codeStr = (d.icd10 || "").trim();
          const codes = codeStr ? codeStr.split(/\s*,\s*/) : [];
          return {
            id: i + 1,
            drugName: d.drugName || "",
            strength: d.strength || "",
            quantity: d.quantity || "1",
            packSize: d.packSize || "",
            icd10Options: codes,
            icd10: codes[0] || "",
            nappiCode: d.nappiCode || "",
            instructions: d.instructions || "",
            costPrice: d.costPrice || "",
            deliveryOption: "Collect",
          };
        });
      }
    } catch {
      const selectedDrugs = (scriptData?.drugs || []).filter((_, idx) => !!selectedRows[idx]);
      medsForAmend = selectedDrugs.map((d, i) => {
        const codeStr = (d.icd10 || "").trim();
        const codes = codeStr ? codeStr.split(/\s*,\s*/) : [];
        return {
          id: i + 1,
          drugName: d.drugName || "",
          strength: d.strength || "",
          quantity: d.quantity || "1",
          packSize: d.packSize || "",
          icd10Options: codes,
          icd10: codes[0] || "",
          nappiCode: d.nappiCode || "",
          instructions: d.instructions || "",
          costPrice: d.costPrice || "",
          deliveryOption: "Collect",
        };
      });
    }
    navigate("/customer/dispensary/amend-script/new-script", {
      state: {
        scriptNo,
        selectedRows,
        amendFlow: true,
        profileNo,
        patientId,
        medications: medsForAmend,
        originalDrugs: originalDrugsMeta,
        removedDrugs: removedDrugsMeta,
        initialScriptType: scriptData?.scriptType || "",
        // Pass through chronic/repeat metadata so the amend form can
        // pre-populate the chronic and repeat checkboxes and count.
        initialIsChronic:
          scriptData?.chronic ?? scriptData?.isChronic ?? false,
        initialIsRepeat:
          (scriptData?.repeatscript ?? scriptData?.repeatScript ?? 0) > 0,
        initialRepeatCount:
          Number(scriptData?.repeatscript ?? scriptData?.repeatScript ?? 0) || 0,
        processcriptnumber:
          scriptData?.processscriptnumber
          || scriptData?.processScriptNumber
          || displayScriptNo
          || scriptNo,
        // Expose existing script document URL so amend flows can
        // display it on the payment summary page when no new file
        // is uploaded on the amend-new-script form.
        scriptDocUrl:
          existingDocUrl
          || scriptData?.scriptDocUrl
          || scriptData?.docurl
          || null,
      },
    });
  };

  const handleRewriteScript = () => {
    if (!showSelectColumn) {
      setShowSelectColumn(true);
      return;
    }
    const anySelected = Object.values(selectedRows || {}).some(Boolean);
    if (!anySelected) {
      setShowNoSelectionModal(true);
      return;
    }
    proceedToNewScriptWithSelection();
  };

  const handleConfirmAmendNoDrugs = async () => {
    if (isAmendingNoDrugs) return;
    setIsAmendingNoDrugs(true);
    setAmendError("");
    try {
      const details = await fetchScriptDetails(scriptNo);
      const fullDrugs = Array.isArray(details?.drugs) ? details.drugs : [];
      const script = details?.script || {};

      let pharmacistid = null;
      try {
        const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]));
          pharmacistid = payload?.userid || null;
        }
      } catch {
        pharmacistid = null;
      }

      const amendRemovedDrugs = fullDrugs.map((d) => ({
        prescriptionDrugId: d.prescriptionDrugId || null,
        sku: d.sku || null,
        quantity: d.quantity || 0,
      }));

      const payload = {
        profilenumber: profileNo || script.profilenumber || script.profileNo || null,
        patientid: patientId || script.patientid || script.patientId || null,
        practitionerid: script.practitionerid || script.practitionerId || null,
        pharmacistid,
        paymenttype: script.paymenttype || script.paymentType || null,
        chronic: !!(script.chronic ?? script.isChronic),
        repeatscript: script.repeatscript ?? script.repeatScript ?? 0,
        cashpricingoption: script.scriptType || script.scripttype || null,
        processscriptnumber:
          script.processscriptnumber
          || script.processScriptNumber
          || script.scriptNumber
          || displayScriptNo
          || scriptNo
          || null,
        branchcode: script.branchcode || null,
        deliveryoption: script.deliveryoption || "Collect",
        preferreddate: null,
        preferredtime: null,
        dispensingfee: null,
        gross: null,
        items: [],
        amendFlow: true,
        amendOriginalPrescriptionId:
          script.id
          || script.prescriptionid
          || script.scriptId
          || scriptNo
          || null,
        docurl: null,
        amendRemovedDrugs,
      };

      const resp = await createProcessPrescription(payload);
      if (resp?.success === false) {
        throw new Error(resp?.message || "Failed to amend prescription");
      }

      const prescriptionId = resp?.data?.prescriptionid || null;
      const receiptNumber = resp?.data?.receiptNumber || null;
      setCompletedMeta({ prescriptionId, receiptNumber });
      setShowNoSelectionModal(false);
      setShowCompleted(true);
    } catch (e) {
      setAmendError(e?.message || "Failed to amend prescription");
    } finally {
      setIsAmendingNoDrugs(false);
    }
  };

  const handleCheckboxChange = (drugIndex) => {
    setSelectedRows((prev) => {
      const newState = {
        ...prev,
        [drugIndex]: !prev[drugIndex],
      };
      return newState;
    });
  };

  return (
    <>
      <PatientScriptInfo
        hideFields={["patient", "medicalAid", "medicalAidNumber"]}
        extraFields={extraFields}
        title="Amend Script Details"
        customButtons={[
          {
            label: showSelectColumn ? "Proceed" : "Rewrite Script",
            onClick: handleRewriteScript,
            variant: "primary",
            disabled: rewriteDisabled,
          },
        ]}
        showSelectColumn={showSelectColumn}
        selectedRows={selectedRows}
        onCheckboxChange={handleCheckboxChange}
      />

      {showNoSelectionModal && (
        <Modal
          onClose={() => {
            if (!isAmendingNoDrugs) setShowNoSelectionModal(false);
          }}
          closeStyle="plain"
        >
          <div style={{ padding: "24px", maxWidth: "420px" }}>
            <h2 style={{ marginBottom: "12px" }}>No items selected</h2>
            <p style={{ marginBottom: "20px" }}>
              You have not selected any drugs on this script. Do you want to proceed to the
              amend script form, or amend this script with no items?
            </p>
            {amendError && (
              <p style={{ color: "#b00020", marginBottom: "12px" }}>{amendError}</p>
            )}
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <Button
                variant="secondary"
                onClick={() => {
                  if (isAmendingNoDrugs) return;
                  setShowNoSelectionModal(false);
                  proceedToNewScriptWithSelection();
                }}
                disabled={isAmendingNoDrugs}
              >
                Go to Amend Form
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmAmendNoDrugs}
                disabled={isAmendingNoDrugs}
              >
                Amend With No Drugs
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showCompleted && (
        <CompletedPopup
          isOpen={showCompleted}
          onClose={() => {
            setShowCompleted(false);
            navigate("/customer/dispensary/ammend-script");
          }}
          prescriptionId={completedMeta.prescriptionId}
          receiptNumber={completedMeta.receiptNumber}
          totals={null}
          barcodeValue={null}
          donePath="/customer/dispensary/ammend-script"
        />
      )}
    </>
  );
}